import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowDown, Copy, Download } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button, SearchField, Select } from "@studio";
import { api } from "../lib/api";
import { errorKindNames, fmtDateTime, num, runLabel, taskLabel, taskTypeNames } from "../lib/format";
import { keys, LIVE } from "../lib/queries";
import type { RunDetail, TaskDetail, TaskEvent } from "../lib/types";
import { InfoRow, Panel, StatusBadge } from "../console/ui";
import { failureLabels, PublicationEvidence, PublishProgress, TenantRoute, type Publication } from "./review";
import { AiCallList } from "@studio-features/meta-crew/llm-usage";
import { useCan } from "../lib/access";
import { toCall } from "../lib/llm-api";
import { ReadableDetails } from "@studio-features/meta-crew/readable-details";
import { errorDetailView } from "../console/readable";

/**
 * 직원 실행 상세 탭 (시안 c9ee685 execution-detail.tsx · execution-log.tsx, 요청서 7-3).
 * 다이얼로그 틀(제목·하단 동작)은 runs.tsx RunDetailDialog 가 그리고, 여기서는 고정 요약 + 탭 + 탭 본문만 그린다.
 */

export type RunTab = "개요" | "실시간 로그" | "결과 증거" | "작업 정보" | "실행 위치" | "실행 기록" | "AI 호출" | "대화";

/** 실행 상세 › AI 호출 (직원, USAGE_READ): 그 작업의 AI 호출 이력 (시안 ba633a0 AiCallList). */
function RunAiCalls({ taskId, visible }: { taskId: string; visible: boolean }) {
  const q = useQuery({ queryKey: ["admin", "llm", "calls", taskId], queryFn: () => api.page<Parameters<typeof toCall>[0]>(`/admin/llm/calls?taskId=${taskId}&limit=200`), enabled: visible, refetchInterval: visible ? LIVE : false });
  if (!q.data) return <div className="lwe-image-state">{q.error ? "AI 호출 이력을 불러오지 못했습니다." : "불러오는 중"}</div>;
  if (!q.data.items.length) return <div className="lwe-image-state">아직 기록된 AI 호출이 없습니다. 로컬 세션 작업은 끝날 때 합계 1건으로 기록됩니다.</div>;
  return <AiCallList calls={q.data.items.map(toCall)} compact />;
}

const ACTIVE_TASK = ["queued", "assigned", "running"];
const ACTIVE_RUN = ["queued", "running", "waiting_machine"];
export const isActiveRun = (r: { status: string }, t?: { status: string } | null) => (t ? ACTIVE_TASK.includes(t.status) : ACTIVE_RUN.includes(r.status));

/** 진입 기본 탭: 진행 중 → 로그, 승인 대기 고객 요청 → 대화, 검토 대기 결과 → 증거, 나머지 → 개요. */
export function defaultRunTab(r: RunDetail, t: TaskDetail | null | undefined, pub: Publication | null): RunTab {
  if (r.origin === "customer" && r.status === "held") return "대화";
  if (isActiveRun(r, t)) return "실시간 로그";
  if (pub?.resultReview?.status === "pending") return "결과 증거";
  return "개요";
}

/**
 * 진행 로그 전체: afterSeq 로 이어 읽어 쌓는다 (한 번에 500건, 서버 상한은 전체 상한이 아님 — 인수인계 2).
 * 키가 작업 키 아래라 실시간 신호(task)로 바로 다시 읽고, 끝난 작업은 주기 갱신을 멈춘다.
 */
export function useTaskEventLog(customerId: string | null, taskId: string | null, live: boolean) {
  const store = useRef<{ key: string; items: TaskEvent[] }>({ key: "", items: [] });
  const key = `${customerId}/${taskId}`;
  if (store.current.key !== key) store.current = { key, items: [] };
  return useQuery({
    queryKey: [...keys.taskEvents(customerId ?? "", taskId ?? ""), "log"],
    enabled: !!customerId && !!taskId,
    refetchInterval: live ? LIVE : false,
    queryFn: async () => {
      const s = store.current;
      for (let i = 0; i < 40; i++) {
        const after = s.items.at(-1)?.seq ?? 0;
        const page = await api.page<TaskEvent>(`/c/${customerId}/tasks/${taskId}/events?afterSeq=${after}`);
        const fresh = page.items.filter((e) => e.seq > after);
        if (fresh.length) s.items = [...s.items, ...fresh];
        if (page.items.length < 500) break;
      }
      return s.items;
    },
  });
}

type LogLine = { seq: number; at: string; source: "브릿지" | "서버"; level: "info" | "warning" | "error"; stage: string; text: string };

/** 서버 이벤트 → 화면 줄. 단계 보고(kind=stage)가 나올 때마다 구분선 이름이 바뀐다. */
function toLines(events: TaskEvent[]): LogLine[] {
  let stage = "시작";
  return events.map((e) => {
    if (e.kind === "stage" && e.text) stage = e.text;
    const text = e.text || e.kind;
    const level = e.kind === "error" || /failed|실패|오류/.test(text) ? "error" : /재시도|retry|경고|지연|warn/i.test(text) ? "warning" : "info";
    return { seq: e.seq, at: e.at, source: e.source === "server" ? "서버" : "브릿지", level, stage, text };
  });
}

const hms = (s: string) => {
  const d = new Date(s);
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, "0")).join(":");
};

/** 실시간 로그 탭 (시안 ExecutionLog 를 실제 이벤트로). 탭을 옮겨도 필터·따라가기 상태는 남는다 (탭 본문을 hidden 으로 둠). */
export function ExecutionLog({ events, active, startedAt, visible: shown, fileName }: { events: TaskEvent[]; active: boolean; startedAt: string | null; visible: boolean; fileName: string }) {
  const entries = toLines(events);
  const [follow, setFollow] = useState(true);
  const [seen, setSeen] = useState(entries.length);
  const [source, setSource] = useState("전체");
  const [query, setQuery] = useState("");
  const [clock, setClock] = useState("absolute");
  const [note, setNote] = useState("");
  const viewport = useRef<HTMLDivElement>(null);
  const q = query.trim().toLowerCase();
  const visible = entries.filter((e) => (source === "전체" || e.source === source) && (!q || e.text.toLowerCase().includes(q)));
  const count = Math.max(0, entries.length - seen);
  const base = new Date(startedAt ?? entries[0]?.at ?? Date.now()).getTime();
  useEffect(() => {
    if (follow && viewport.current) viewport.current.scrollTop = viewport.current.scrollHeight;
    if (follow) setSeen(entries.length);
  }, [entries.length, follow, source, query, shown]);
  const timestamp = (at: string) => {
    if (clock === "absolute") return hms(at);
    const s = Math.max(0, Math.round((new Date(at).getTime() - base) / 1000));
    return `+${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };
  const text = () => visible.map((e) => `${timestamp(e.at)} [${e.source}] ${e.text}`).join("\n");
  return (
    <div className="lwe-log">
      <div className="lwe-log-filters">
        <SearchField showLabel label="로그 검색" placeholder="내용 검색" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Select label="출처" value={source} onChange={(e) => setSource(e.target.value)}>
          {["전체", "브릿지", "서버"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </Select>
        <Select label="시각 표시" value={clock} onChange={(e) => setClock(e.target.value)}>
          <option value="absolute">절대 시각</option>
          <option value="elapsed">시작 후 경과</option>
        </Select>
      </div>
      <div className="lwe-toolbar">
        <label className="lwe-follow">
          <input
            type="checkbox"
            checked={follow}
            onChange={(e) => {
              setFollow(e.target.checked);
              setSeen(entries.length);
            }}
          />
          자동 따라가기
        </label>
        <span className="meta-crew-muted">{follow ? "최신 로그를 따라가는 중" : "이전 로그 읽는 중"}</span>
        <div className="lwe-controls">
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(text());
                setNote("표시 중인 로그를 복사했습니다.");
              } catch {
                setNote("복사하지 못했습니다. 내려받기를 이용해 주세요.");
              }
            }}
          >
            <Copy size={14} />
            복사
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const url = URL.createObjectURL(new Blob([text()], { type: "text/plain;charset=utf-8" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = fileName;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <Download size={14} />
            내려받기
          </Button>
        </div>
      </div>
      <div
        className="lwe-log-scroll"
        ref={viewport}
        tabIndex={0}
        aria-label="실시간 로그 내용"
        onScroll={(e) => {
          const el = e.currentTarget;
          const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 36;
          if (bottom !== follow) {
            setFollow(bottom);
            setSeen(entries.length);
          }
        }}
      >
        {visible.map((entry, i) => (
          <div key={entry.seq}>
            {(i === 0 || visible[i - 1]!.stage !== entry.stage) && <div className="lwe-log-divider">{entry.stage}</div>}
            <div className={`lwe-log-line is-${entry.level} ${entry.source === "서버" ? "is-server" : ""}`}>
              <time>{timestamp(entry.at)}</time>
              <span>{entry.source}</span>
              <p>{entry.text}</p>
            </div>
          </div>
        ))}
        {!visible.length && <p className="lwe-image-state">{entries.length ? "조건에 맞는 로그가 없습니다." : active ? "아직 수신된 로그가 없습니다. 배정되면 바로 이어서 표시합니다." : "기록된 로그가 없습니다."}</p>}
      </div>
      <div className="lwe-log-bottom">
        <small role="status">{note || `${visible.length} / ${entries.length}줄 · ${active ? "실시간 수신 중" : "실행 종료"}`}</small>
        {!follow && (
          <Button
            size="sm"
            onClick={() => {
              setFollow(true);
              setSeen(entries.length);
            }}
          >
            <ArrowDown size={14} />
            {count ? `새 로그 ${count}줄 · 맨 아래로` : "맨 아래로"}
          </Button>
        )}
      </div>
    </div>
  );
}

/** 1초마다 다시 그리는 경과 시간 (진행 중일 때만). */
function useElapsed(from: string | null, to: string | null, live: boolean): string {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [live]);
  if (!from) return "";
  const s = Math.max(0, Math.round(((to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

const stageNames: Record<string, string> = { review: "원고 검토", run: "작업 수행", posted: "작업 완료", verify: "완료 검토", done: "최종 보고" };

export function RunDetailTabs({
  run: r,
  task: t,
  customerId,
  pub,
  tab,
  onTab,
  hidden,
  onMachine,
  overview,
  info,
  conversation,
  unread,
  customerName,
}: {
  customerName?: string;
  run: RunDetail;
  task: TaskDetail | null | undefined;
  customerId: string | null;
  pub: Publication | null;
  tab: RunTab;
  onTab: (t: RunTab) => void;
  hidden: boolean;
  onMachine?: () => void;
  /** 개요 탭 아래쪽 (결과 요약·승인 안내 등 RunDetailDialog 가 가진 내용). */
  overview?: ReactNode;
  /** 작업 정보 탭 아래쪽 (원고·SEO 키워드). */
  info?: ReactNode;
  /** 대화 탭 (고객 요청만: 승인 패널 + 스레드). */
  conversation?: ReactNode;
  unread?: number;
}) {
  const active = isActiveRun(r, t);
  const log = useTaskEventLog(customerId, t?.id ?? null, active);
  const elapsed = useElapsed(t?.startedAt ?? r.startedAt ?? null, t?.finishedAt ?? (active ? null : (r.finishedAt ?? null)), active);
  const canAi = useCan("USAGE_READ");
  const tabs: RunTab[] = ["개요", "실시간 로그", "결과 증거", "작업 정보", "실행 위치", "실행 기록", ...(canAi && t ? (["AI 호출"] as const) : []), ...(r.origin === "customer" ? (["대화"] as const) : [])];
  const selected = tabs.includes(tab) ? tab : "개요";
  const captures = pub?.screenshots?.length ?? 0;
  const stage = t?.result?.stage ?? r.stage;
  const usage = t?.result?.usage as Record<string, number | string> | undefined;
  const machine = t?.machineLabel ?? null;
  const device = t?.assignedDeviceSerial ?? null;
  const subject = pub?.subject ?? (t?.targetCustomer ? { customerId: t.targetCustomer.id, name: t.targetCustomer.name } : null);
  const executorName = customerName ?? pub?.executor.name ?? "실행 고객사";
  return (
    <div className="lwe-run-shell">
      <div className="lwe-run-summary">
        <StatusBadge label={t ? taskLabel(t.status) : runLabel(r.status)} />
        {(stage || active) && <strong>{stage ? stageNames[stage] : t && t.status !== "queued" ? "작업 수행" : "배정 대기"}</strong>}
        <span>
          {active ? (t?.startedAt ? `${elapsed} 경과` : "배정 대기") : elapsed ? `소요 ${elapsed}` : "실행 종료"}
          {r.kind === "machine_task" ? ` · ${machine ?? "배정 전"} / ${device ?? "디바이스 미정"}` : " · 코어 작업"}
        </span>
        <small>{r.id.slice(0, 8)}</small>
      </div>
      <div
        className="lwe-run-tabs"
        role="tablist"
        aria-label="실행 상세 탭"
        onKeyDown={(e) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
          e.preventDefault();
          const i = tabs.indexOf(selected);
          const next = e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : (i + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
          onTab(tabs[next]!);
          (e.currentTarget.children[next] as HTMLElement).focus();
        }}
      >
        {tabs.map((x, i) => (
          <button key={x} type="button" role="tab" id={`run-tab-${i}`} aria-controls={`run-panel-${i}`} aria-selected={selected === x} tabIndex={selected === x ? 0 : -1} onClick={() => onTab(x)}>
            {x}
            {x === "실시간 로그" && active && <span className="lwe-live-dot" aria-label="진행 중" />}
            {x === "결과 증거" && <span className="lwe-tab-count">{captures}</span>}
            {x === "대화" && !!unread && <span className="lwe-tab-count">{unread}</span>}
          </button>
        ))}
      </div>
      {tabs.map((x, i) => (
        <section key={x} role="tabpanel" id={`run-panel-${i}`} aria-labelledby={`run-tab-${i}`} hidden={selected !== x} className={`lwe-run-panel ${x === "실시간 로그" ? "is-log" : ""}`}>
          {x === "개요" && (
            <div className="lwd-stack">
              {t?.type === "naver_blog_post" && <PublishProgress stage={stage ?? null} failure={pub?.failure ?? null} succeeded={t.status === "succeeded"} />}
              <Panel title="실행 결과">
                <div className="lwd-panel-body">
                  <h3>{pub?.failure ? failureLabels[pub.failure] : t ? taskLabel(t.status)[0] : runLabel(r.status)[0]}</h3>
                  <p>{t?.result?.summary ?? r.resultSummary ?? t?.error ?? r.errorMessage ?? (active ? "진행 중입니다. 실시간 로그 탭에서 이어서 볼 수 있습니다." : "결과 요약이 없습니다.")}</p>
                  {(["failed", "dead"].includes(r.status) || pub?.failure) && (
                    <p className="lwp-callout">
                      {pub?.failure ? "게시 여부와 결과 증거를 먼저 확인하세요. 자동 재게시하지 않습니다." : "연결과 인증 상태를 복구한 뒤 재시도를 요청하세요."}
                    </p>
                  )}
                </div>
              </Panel>
              {overview}
            </div>
          )}
          {x === "실시간 로그" && (
            <ExecutionLog events={log.data ?? []} active={active} startedAt={t?.startedAt ?? null} visible={selected === "실시간 로그" && !hidden} fileName={`meta-crew-log-${(t?.id ?? r.id).slice(0, 8)}.txt`} />
          )}
          {x === "AI 호출" && t && <RunAiCalls taskId={t.id} visible={selected === "AI 호출" && !hidden} />}
          {x === "결과 증거" && (pub ? <PublicationEvidence p={pub} /> : <div className="lwe-image-state">아직 수신된 결과 증거가 없습니다.</div>)}
          {x === "작업 정보" && (
            <div className="lwd-stack">
              <Panel title="작업 정보">
                <dl className="lwe-definition">
                  <dt>유형 / 실행</dt>
                  <dd>
                    {t ? (taskTypeNames[t.type] ?? t.type) : r.kind === "system" ? "시스템 작업" : "작업"} · {r.attempt}번째 시도
                  </dd>
                  <dt>등록</dt>
                  <dd>{fmtDateTime(t?.createdAt ?? r.createdAt)}</dd>
                  <dt>시작 / 종료</dt>
                  <dd>
                    {fmtDateTime(t?.startedAt ?? r.startedAt)} / {active ? "진행 중" : fmtDateTime(t?.finishedAt ?? r.finishedAt)}
                  </dd>
                  {t?.errorKind && (
                    <>
                      <dt>실패 분류</dt>
                      <dd>{failureLabels[t.errorKind] ?? errorKindNames[t.errorKind] ?? t.errorKind}</dd>
                    </>
                  )}
                  <dt>AI 모델</dt>
                  <dd>{usage?.model ? String(usage.model) : r.kind === "machine_task" ? (active ? "실행 중 · 끝나면 집계" : "기록 없음") : "사용하지 않음"}</dd>
                  <dt>입력 / 캐시 / 출력 토큰</dt>
                  <dd>{usage ? `${num(Number(usage.inputTokens ?? 0))} / ${num(Number(usage.cachedInputTokens ?? 0))} / ${num(Number(usage.outputTokens ?? 0))}` : "—"}</dd>
                </dl>
              </Panel>
              {t?.instructions && (
                <Panel title="작업 지시">
                  <pre className="lwe-manuscript">{t.instructions}</pre>
                </Panel>
              )}
              {info}
            </div>
          )}
          {x === "실행 위치" && (
            <div className="lwd-stack">
              {subject && <TenantRoute executor={{ name: executorName }} subject={subject} crew={!!t?.crew} />}
              <Panel title="배정된 실행 위치">
                <dl className="lwe-definition">
                  <dt>고객사</dt>
                  <dd>{executorName}</dd>
                  <dt>머신</dt>
                  <dd>{r.kind === "system" ? "코어 (서버 작업)" : machine ? `${machine} · ${active ? "실행 중" : "마지막 실행 위치"}` : "배정 전"}</dd>
                  <dt>디바이스</dt>
                  <dd>{r.kind === "system" ? "없음 · 서버 작업" : (device ?? "자동 배정")}</dd>
                  <dt>블로그 계정</dt>
                  <dd>{pub?.executor.blogId ?? "배정 후 확인"}</dd>
                </dl>
              </Panel>
              {onMachine && t?.machineId && (
                <div className="lwd-actions">
                  <Button variant="secondary" onClick={onMachine}>
                    머신 상세 열기
                  </Button>
                </div>
              )}
            </div>
          )}
          {x === "실행 기록" && (
            <div className="lwd-stack">
              <Panel title="Run 이벤트">
                {r.events.map((e, n) => (
                  <InfoRow key={n} title={`${fmtDateTime(e.at)} · ${e.kind}`} description={e.message ?? ""} />
                ))}
                {r.events.length === 0 && <InfoRow title="기록 없음" description="아직 실행 기록이 없습니다." />}
              </Panel>
              {(r.errorDetail != null || r.errorMessage) && (
                <ReadableDetails view={errorDetailView({ errorKind: r.errorKind, error: r.errorMessage, errorDetail: r.errorDetail }, true)} canReadDebug />
              )}
            </div>
          )}
          {x === "대화" && conversation}
        </section>
      ))}
    </div>
  );
}
